import React, { useState, useEffect, useCallback } from 'react';

// CVSS v3.1 metric definitions
const METRICS = {
  AV: { name: 'Attack Vector', options: [
    { value: 'N', label: 'Network', score: 0.85 },
    { value: 'A', label: 'Adjacent', score: 0.62 },
    { value: 'L', label: 'Local', score: 0.55 },
    { value: 'P', label: 'Physical', score: 0.20 },
  ]},
  AC: { name: 'Attack Complexity', options: [
    { value: 'L', label: 'Low', score: 0.77 },
    { value: 'H', label: 'High', score: 0.44 },
  ]},
  PR: { name: 'Privileges Required', options: [
    { value: 'N', label: 'None', score: 0.85, scoreChanged: 0.85 },
    { value: 'L', label: 'Low', score: 0.62, scoreChanged: 0.68 },
    { value: 'H', label: 'High', score: 0.27, scoreChanged: 0.50 },
  ]},
  UI: { name: 'User Interaction', options: [
    { value: 'N', label: 'None', score: 0.85 },
    { value: 'R', label: 'Required', score: 0.62 },
  ]},
  S: { name: 'Scope', options: [
    { value: 'U', label: 'Unchanged', score: 0 },
    { value: 'C', label: 'Changed', score: 1 },
  ]},
  C: { name: 'Confidentiality', options: [
    { value: 'H', label: 'High', score: 0.56 },
    { value: 'L', label: 'Low', score: 0.22 },
    { value: 'N', label: 'None', score: 0 },
  ]},
  I: { name: 'Integrity', options: [
    { value: 'H', label: 'High', score: 0.56 },
    { value: 'L', label: 'Low', score: 0.22 },
    { value: 'N', label: 'None', score: 0 },
  ]},
  A: { name: 'Availability', options: [
    { value: 'H', label: 'High', score: 0.56 },
    { value: 'L', label: 'Low', score: 0.22 },
    { value: 'N', label: 'None', score: 0 },
  ]},
};

const METRIC_ORDER = ['AV', 'AC', 'PR', 'UI', 'S', 'C', 'I', 'A'];

function parseVector(vector) {
  if (!vector) return {};
  const parsed = {};
  const parts = vector.replace(/^CVSS:3\.[01]\//, '').split('/');
  parts.forEach(p => {
    const [key, val] = p.split(':');
    if (key && val) parsed[key] = val;
  });
  return parsed;
}

function buildVector(selections) {
  const parts = METRIC_ORDER
    .filter(k => selections[k])
    .map(k => `${k}:${selections[k]}`);
  if (parts.length === 0) return '';
  return `CVSS:3.1/${parts.join('/')}`;
}

/** The chosen option for a metric, or undefined if the value is not one of them. */
function optionFor(metric, selections) {
  return METRICS[metric].options.find(o => o.value === selections[metric]);
}

function calculateScore(selections) {
  // Need all 8 base metrics, and each has to be a value the metric defines.
  // Presence alone was not enough: the vector is a free-text box, so a typo
  // such as AV:X or a stray character on the end of the last metric produced a
  // selection that was truthy but matched no option, and reading .score off
  // that undefined threw -- taking the whole page down rather than showing no
  // score. An unrecognised vector means the same thing as an incomplete one.
  const chosen = {};
  for (const k of METRIC_ORDER) {
    const opt = optionFor(k, selections);
    if (!opt) return null;
    chosen[k] = opt;
  }

  const scopeChanged = selections.S === 'C';

  const av = chosen.AV.score;
  const ac = chosen.AC.score;
  const pr = scopeChanged ? chosen.PR.scoreChanged : chosen.PR.score;
  const ui = chosen.UI.score;
  const c = chosen.C.score;
  const i = chosen.I.score;
  const a = chosen.A.score;

  const iss = 1 - ((1 - c) * (1 - i) * (1 - a));

  let impact;
  if (scopeChanged) {
    impact = 7.52 * (iss - 0.029) - 3.25 * Math.pow(iss - 0.02, 15);
  } else {
    impact = 6.42 * iss;
  }

  if (impact <= 0) return 0;

  const exploitability = 8.22 * av * ac * pr * ui;

  let score;
  if (scopeChanged) {
    score = Math.min(1.08 * (impact + exploitability), 10);
  } else {
    score = Math.min(impact + exploitability, 10);
  }

  // Round up to nearest 0.1
  return Math.ceil(score * 10) / 10;
}

function getSeverity(score) {
  if (score === null || score === undefined) return null;
  if (score === 0) return 'Info';
  if (score <= 3.9) return 'Low';
  if (score <= 6.9) return 'Medium';
  if (score <= 8.9) return 'High';
  return 'Critical';
}

function getSeverityColor(severity) {
  switch (severity) {
    case 'Critical': return '#dc2626';
    case 'High': return '#ea580c';
    case 'Medium': return '#d97706';
    case 'Low': return '#2563eb';
    case 'Info': return '#6b7280';
    default: return '#6b7280';
  }
}

export default function CvssCalculator({ value, onChange }) {
  const [selections, setSelections] = useState({});
  const [expanded, setExpanded] = useState(false);

  // Parse incoming vector string into selections
  useEffect(() => {
    if (value?.vector) {
      const parsed = parseVector(value.vector);
      setSelections(prev => {
        const newParsed = parseVector(value.vector);
        if (JSON.stringify(prev) !== JSON.stringify(newParsed)) return newParsed;
        return prev;
      });
    }
  }, [value?.vector]);

  const handleSelect = useCallback((metric, val) => {
    setSelections(prev => {
      const next = { ...prev, [metric]: val };
      const score = calculateScore(next);
      const vector = buildVector(next);
      const severity = getSeverity(score);
      // Defer the onChange to avoid updating parent during render
      setTimeout(() => onChange({ score, vector, severity }), 0);
      return next;
    });
  }, [onChange]);

  const score = calculateScore(selections);
  const severity = getSeverity(score);
  const vector = buildVector(selections);
  const filledCount = METRIC_ORDER.filter(k => selections[k]).length;

  const btnBase = {
    padding: '4px 10px', fontSize: 12, fontWeight: 500, border: '1px solid var(--border)',
    borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s',
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: expanded ? 12 : 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              ...btnBase, background: 'var(--primary)', color: '#fff', border: 'none',
              padding: '6px 14px', fontSize: 13,
            }}
          >
            {expanded ? '▾ CVSS Calculator' : '▸ CVSS Calculator'}
          </button>
          {score !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 40, height: 40, borderRadius: '50%', fontWeight: 700, fontSize: 15,
                background: getSeverityColor(severity), color: '#fff',
              }}>
                {score.toFixed(1)}
              </span>
              <span style={{
                fontSize: 13, fontWeight: 600, color: getSeverityColor(severity),
              }}>
                {severity}
              </span>
            </div>
          )}
          {!expanded && score === null && filledCount > 0 && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {filledCount}/8 metrics selected
            </span>
          )}
        </div>
        {score !== null && !expanded && (
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
            {vector}
          </span>
        )}
      </div>

      {expanded && (
        <div style={{
          background: 'var(--bg-input)', borderRadius: 10, padding: 16,
          border: '1px solid var(--border)',
        }}>
          {/* Score display */}
          {score !== null && (
            <div style={{
              textAlign: 'center', marginBottom: 16, padding: '10px 0',
              borderBottom: '1px solid var(--border)',
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4, fontFamily: 'monospace' }}>
                {vector}
              </div>
            </div>
          )}

          {/* Metric groups */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {METRIC_ORDER.map(key => {
              const metric = METRICS[key];
              return (
                <div key={key}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
                    {metric.name} ({key})
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {metric.options.map(opt => {
                      const isSelected = selections[key] === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => handleSelect(key, opt.value)}
                          style={{
                            ...btnBase,
                            background: isSelected ? getSeverityColor(
                              key === 'S' ? (opt.value === 'C' ? 'High' : 'Low') :
                              opt.score >= 0.56 ? 'Critical' :
                              opt.score >= 0.44 ? 'High' :
                              opt.score >= 0.22 ? 'Medium' :
                              opt.score > 0 ? 'Low' : 'Info'
                            ) : 'var(--bg-card)',
                            color: isSelected ? '#fff' : 'var(--text-primary)',
                            borderColor: isSelected ? 'transparent' : 'var(--border)',
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Reset */}
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button
              onClick={() => {
                setSelections({});
                onChange({ score: null, vector: '', severity: null });
              }}
              style={{ ...btnBase, background: 'transparent', color: 'var(--text-secondary)', fontSize: 11 }}
            >
              Reset Calculator
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
